import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip } from "@/components/ui-bits";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import { Megaphone, Cake, Pin, PinOff, Trash2, Plus, X, User, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const DISMISS_KEY = "rostr:news:dismissed:v1";
const readDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
};
const writeDismissed = (set) => {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch {}
};

// Shared company news feed. Managers can post announcements; everyone reads.
// Birthdays are automatically interleaved (from /api/today).
export default function News() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isManager = ["owner", "manager", "teamlead"].includes(user?.role);
  const [posts, setPosts] = useState([]);
  const [today, setToday] = useState(null);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  const dismissPost = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      writeDismissed(next);
      return next;
    });
  };

  const restoreAll = () => {
    setDismissed(new Set());
    writeDismissed(new Set());
    toast.success(t("news_restored"));
  };

  const load = async () => {
    try {
      const [p, td] = await Promise.all([api.get("/news"), api.get("/today")]);
      setPosts(p.data);
      setToday(td.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submitPost = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error(t("title_body_required"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/news", form);
      setForm({ title: "", body: "", pinned: false });
      setComposing(false);
      toast.success(t("post_published"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const togglePin = async (p) => {
    try {
      await api.patch(`/news/${p._id}`, { pinned: !p.pinned });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const [confirmDel, setConfirmDel] = useState(null);
  const del = (p) => setConfirmDel(p);
  const doDelete = async () => {
    const p = confirmDel;
    setConfirmDel(null);
    if (!p) return;
    try {
      await api.delete(`/news/${p._id}`);
      toast.success(t("deleted"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const bdaysToday = today?.birthdays_today || [];
  const bdaysMonth = today?.birthdays_month || [];
  const canModify = (p) => user && (user.role === "owner" || p.author_id === user._id);
  const visiblePosts = useMemo(() => posts.filter((p) => !dismissed.has(p._id)), [posts, dismissed]);
  const hiddenCount = posts.length - visiblePosts.length;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6" data-testid="page-news">
      <PageHeader
        eyebrow={t("company_label")}
        title={t("news_page_title")}
        subtitle={t("news_page_subtitle")}
        actions={isManager && (
          <button onClick={() => setComposing((v) => !v)} className="btn btn-primary" data-testid="news-compose-btn">
            {composing ? <><X size={14} /> {t("cancel")}</> : <><Plus size={14} /> {t("new_post")}</>}
          </button>
        )}
      />

      {/* Composer */}
      <AnimatePresence>
        {composing && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="card p-5 md:p-6" data-testid="news-composer"
          >
            <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] mb-3">{t("new_post")}</div>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t("news_title_placeholder")}
              maxLength={140}
              className="input-field !text-[15px] !font-semibold mb-3"
              data-testid="news-title"
            />
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder={t("news_body_placeholder")}
              rows={5}
              className="input-field !py-3 leading-relaxed resize-y"
              data-testid="news-body"
            />
            <div className="flex items-center justify-between mt-4">
              <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none">
                <input type="checkbox" checked={form.pinned}
                       onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                       data-testid="news-pin-toggle" />
                <span className="flex items-center gap-1.5"><Pin size={12} /> {t("pin_top")}</span>
              </label>
              <button onClick={submitPost} disabled={busy} className="btn btn-primary" data-testid="news-submit">
                {busy ? t("posting") : t("post")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Birthdays today — always highlighted */}
      {bdaysToday.length > 0 && (
        <div className="card p-5 md:p-6 border-l-4" style={{ borderLeftColor: "hsl(30 92% 55%)" }} data-testid="news-bday-today">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(45_92%_60%)] to-[hsl(30_92%_50%)] text-white grid place-items-center">
              <Cake size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">
                🎂 {t("birthday_today_title")}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("congratulate")}</div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {bdaysToday.map((b) => (
              <BirthdayCelebrateRow key={b._id} bday={b} currentUserId={user?._id} />
            ))}
          </div>
        </div>
      )}

      {/* News posts */}
      <div className="flex flex-col gap-4">
        {visiblePosts.length === 0 && bdaysMonth.length === 0 && bdaysToday.length === 0 && (
          <NewsEmptyState
            t={t}
            hasDismissed={hiddenCount > 0}
            hiddenCount={hiddenCount}
            onRestore={restoreAll}
            isManager={isManager}
          />
        )}
        <AnimatePresence initial={false}>
        {visiblePosts.map((p) => (
          <SwipeablePost key={p._id} onDismiss={() => dismissPost(p._id)}>
          <motion.article
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`card p-5 md:p-6 ${p.pinned ? "ring-1 ring-[hsl(var(--primary))]" : ""}`}
            data-testid={`news-post-${p._id}`}
          >
            <div className="flex items-start gap-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
                <User size={14} strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {p.pinned && <Chip variant="primary"><Pin size={10} className="mr-1" />{t("pinned_label")}</Chip>}
                  <span className="text-[12px] font-semibold text-[hsl(var(--text-strong))]">{p.author_name}</span>
                  {p.author_role && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">· {p.author_role}</span>}
                </div>
                <div className="text-[11px] text-[hsl(var(--text-muted))]">
                  {new Date(p.created_at).toLocaleString("nl", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              {canModify(p) && (
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePin(p)} className="btn btn-ghost !h-8" data-testid={`news-pin-${p._id}`}>
                    {p.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                  </button>
                  <button onClick={() => del(p)} className="btn btn-ghost !h-8" data-testid={`news-del-${p._id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
            <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))] tracking-tight leading-tight mb-2">
              {p.title}
            </div>
            <div className="text-[14px] text-[hsl(var(--text-strong))] leading-relaxed whitespace-pre-wrap">
              {p.body}
            </div>
          </motion.article>
          </SwipeablePost>
        ))}
        </AnimatePresence>

        {/* Restore hidden posts */}
        {hiddenCount > 0 && visiblePosts.length > 0 && (
          <button
            onClick={restoreAll}
            data-testid="news-restore-hidden"
            className="mx-auto mt-1 text-[12px] font-medium text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] transition underline underline-offset-4"
          >
            {t("news_restore_hidden_prefix")} {hiddenCount} {hiddenCount === 1 ? t("news_restore_hidden_one") : t("news_restore_hidden_many")}
          </button>
        )}

        {/* Upcoming birthdays this month card */}
        {bdaysMonth.length > 0 && (
          <div className="card p-5 md:p-6" data-testid="news-bday-month">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[hsl(45_92%_92%)] text-[hsl(30_92%_45%)] grid place-items-center">
                <Cake size={18} strokeWidth={1.75} />
              </div>
              <div>
                <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("birthdays_month_title")}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("birthdays_month_hint")}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {bdaysMonth.map((b) => {
                const [mm, dd] = (b.mmdd || "").split("-");
                const dt = new Date(new Date().getFullYear(), parseInt(mm, 10) - 1, parseInt(dd, 10));
                return (
                  <div key={b._id} className="flex items-center gap-3 rounded-xl px-3 py-2 bg-[hsl(var(--muted))]" data-testid={`news-bday-m-${b._id}`}>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(45_92%_60%)] to-[hsl(30_92%_50%)] grid place-items-center text-white text-[11px] font-bold shrink-0">
                      {b.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{b.name}</div>
                      {b.job_title && <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{b.job_title}</div>}
                    </div>
                    <span className="text-[11px] font-mono text-[hsl(var(--text-muted))] shrink-0">
                      {dt.toLocaleDateString("nl", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        title={t("confirm_delete_title")}
        body={confirmDel ? (confirmDel.title || t("delete_post_confirm")) : ""}
        confirmLabel={t("delete")}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
        testid="confirm-news"
      />
    </div>
  );
}
function SwipeablePost({ children, onDismiss }) {
  const { t } = useI18n();
  const [dragX, setDragX] = useState(0);
  const [gone, setGone] = useState(false);
  const threshold = -110;

  return (
    <motion.div
      layout
      className="relative touch-pan-y"
      initial={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0, transition: { duration: 0.25 } }}
    >
      {/* Trail behind card, revealed on swipe */}
      <div
        className="absolute inset-0 rounded-3xl flex items-center justify-end pr-6 pointer-events-none overflow-hidden"
        style={{
          background: "linear-gradient(90deg, transparent 0%, hsl(0 78% 96%) 40%, hsl(0 78% 90%) 100%)",
          opacity: Math.min(1, Math.abs(Math.min(dragX, 0)) / 120),
        }}
      >
        <div className="flex items-center gap-2 text-[hsl(0_78%_45%)] font-bold text-[13px]">
          <Trash2 size={16} strokeWidth={2} />
          <span>{t("news_hide")}</span>
        </div>
      </div>
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -220, right: 0 }}
        dragElastic={0.15}
        onDrag={(_, info) => setDragX(info.offset.x)}
        onDragEnd={(_, info) => {
          if (info.offset.x < threshold || info.velocity.x < -500) {
            setGone(true);
            // Animate out then unmount via parent state
            setTimeout(() => onDismiss(), 200);
          } else {
            setDragX(0);
          }
        }}
        animate={gone ? { x: -400, opacity: 0 } : { x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="relative cursor-grab active:cursor-grabbing"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

// Empty state for the news feed — friendly illustration when nothing to show.
function NewsEmptyState({ t, hasDismissed, hiddenCount, onRestore, isManager }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="py-14 md:py-16 text-center"
      data-testid="news-empty-state"
    >
      <PaperPlaneEmpty size={150} />
      <div className="font-display text-[22px] md:text-[26px] font-bold text-[hsl(var(--text-strong))] tracking-tight mt-4 mb-2">
        {hasDismissed ? t("news_empty_dismissed_title") : t("no_news_title")}
      </div>
      <div className="text-[14px] text-[hsl(var(--text-muted))] max-w-md leading-relaxed mx-auto">
        {hasDismissed
          ? t("news_empty_dismissed_hint")
          : (isManager ? t("no_news_hint_manager") : t("no_news_hint_employee"))}
      </div>
      {hasDismissed && (
        <button
          onClick={onRestore}
          data-testid="news-empty-restore"
          className="btn btn-ghost mt-5"
        >
          {t("news_restore_all")} · {hiddenCount}
        </button>
      )}
    </motion.div>
  );
}

// Confetti reaction row — inline in News. Only interactable on the actual birthday.
function BirthdayCelebrateRow({ bday, currentUserId }) {
  const { t } = useI18n();
  const [state, setState] = useState({ count: 0, celebrated: false, loading: true });
  const isMe = currentUserId === bday._id;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/birthdays/${bday._id}/celebrations`);
        setState({ count: data.count || 0, celebrated: !!data.celebrated, loading: false });
      } catch { setState((s) => ({ ...s, loading: false })); }
    })();
  }, [bday._id]);

  const celebrate = async () => {
    if (state.celebrated || isMe) return;
    try {
      const { data } = await api.post(`/birthdays/${bday._id}/celebrate`);
      setState({ count: data.count, celebrated: true, loading: false });
      toast.success(t("bday_toast_sent"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("bday_toast_error"));
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-xl px-3 py-2 bg-[hsl(45_92%_96%)]" data-testid={`news-bday-t-${bday._id}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(45_92%_55%)] to-[hsl(30_92%_50%)] grid place-items-center text-white text-[13px] font-bold shrink-0">
        {bday.initials || "?"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))] truncate">{bday.name}</div>
        {bday.job_title && <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{bday.job_title}</div>}
      </div>
      {!isMe && (
        <motion.button
          onClick={celebrate}
          disabled={state.celebrated || state.loading}
          whileTap={{ scale: 0.94 }}
          data-testid={`bday-celebrate-${bday._id}`}
          aria-pressed={state.celebrated}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors shrink-0 ${
            state.celebrated
              ? "bg-[hsl(38_92%_96%)] text-[hsl(28_92%_42%)] border-[hsl(38_92%_84%)]"
              : "bg-transparent text-[hsl(var(--text-strong))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
          }`}
        >
          <PartyPopper size={13} strokeWidth={2} />
          <span>{state.celebrated ? t("bday_celebrated") : t("bday_celebrate")}</span>
          {state.count > 0 && (
            <span className="ml-0.5 tabular-nums text-[11px] font-bold text-[hsl(var(--text-muted))]">· {state.count}</span>
          )}
        </motion.button>
      )}
    </div>
  );
}
